import { type Client as BrowserClient } from '@bugsnag/js';
import { type Client as NodeClient } from '@bugsnag/node';

import { type LoggerReport, type LoggerReporter, type LoggerSeverity } from '@automation-suite/shared-clients-logger';

import { loggerSeverityToBugsnagSeverity } from './utils';

export type BugsnagReporterOptions = {
  client: BrowserClient | NodeClient;
  reportableSeverities: LoggerSeverity[];
};

export class BugsnagReporter implements LoggerReporter {
  private client: BrowserClient | NodeClient;
  readonly reportableSeverities: LoggerSeverity[];

  constructor({ reportableSeverities = ['error', 'warn'], ...options }: BugsnagReporterOptions) {
    this.reportableSeverities = reportableSeverities;
    this.client = options.client;
  }

  public process(report: LoggerReport) {
    let errorToReport: Error | string;
    let customMessage: string | undefined;

    if (report.message instanceof Error) {
      errorToReport = report.message;
    } else {
      const errorFromMetadata = report.metadata?.['error'];
      const errorFromContext = report.context?.['error'];
      const actualError =
        errorFromMetadata instanceof Error
          ? errorFromMetadata
          : errorFromContext instanceof Error
            ? errorFromContext
            : null;

      if (actualError) {
        errorToReport = actualError;
        customMessage = report.message;
      } else {
        errorToReport = new Error(report.message || 'Unknown error');
      }
    }

    this.client.notify(errorToReport, (event) => {
      event.severity = loggerSeverityToBugsnagSeverity(report.severity);

      if (customMessage && event.errors.length > 0) {
        event.errors[0].errorMessage = customMessage;
      }

      if (report.metadata) {
        event.addMetadata('metadata', report.metadata);
      }
      if (report.context) {
        event.addMetadata('moreInfo', report.context);
      }
    });
  }
}
